/**
 * Tiny fetch wrapper for the FastAPI backend used by the Rent UI.
 *
 * Ported from `src/components/rent/api.ts`. Two deliberate changes, both at
 * the transport boundary — every call site keeps the exact path string it had
 * (FE-03: reuse, don't rewrite):
 *
 *  1. **`fetch` -> `apiFetch`.** The SPA authenticates against FastAPI with a
 *     bearer token, and `apiFetch` is what attaches it (plus cookies, plus the
 *     one-401-retry token rotation). A bare `fetch` would 401 every rent call.
 *
 *  2. **The `/py-api/v1` prefix is no longer hardcoded HERE.** The rent module
 *     has been FastAPI-native since v3.0, so its ~150 call sites are already
 *     literally correct — but the prefix a URL is built from must come from
 *     `lib/api/config.ts`, which owns which backend each domain is on and
 *     where it is mounted. `rentApiUrl` therefore strips the legacy literal
 *     off an incoming path and re-attaches the configured FastAPI base. Change
 *     `API_HOST` or `BACKEND_PREFIX.fastapi` and every rent request follows,
 *     without touching a component.
 *
 * Throws on non-2xx with the parsed `detail` (FastAPI `HTTPException`) or
 * `error` (the `UploadRejected` envelope `document.py`/`vault.py`/
 * `warranty.py` all return for a rejected upload — size cap, disallowed
 * type, magic-number mismatch) field as message. Before `error` was read
 * here, every upload rejection surfaced as a bare "Request failed (413)"
 * toast — the real reason (e.g. "file is larger than the 15 MB limit") was
 * parsed and then discarded.
 */
import { apiFetch } from "@/lib/api/client";
import { API_HOST, BACKEND_PREFIX } from "@/lib/api/config";

const ADMIN_VIEW_STORAGE_KEY = "aim-rent-admin-view-all";

/** The literal every ported rent call site still writes. Never sent as-is. */
const LEGACY_FASTAPI_PREFIX = "/py-api/v1";

/** Configured FastAPI base for the rent domains, e.g. `""` + `/py-api/v1`. */
const FASTAPI_BASE = `${API_HOST}${BACKEND_PREFIX.fastapi}`;

/**
 * Map a rent path onto the configured FastAPI base.
 *
 * `"/py-api/v1/properties?size=200"` -> `` `${FASTAPI_BASE}/properties?size=200` ``
 * A path that does not carry the legacy prefix (a Next.js `/api/...` route, or
 * an absolute URL) is returned untouched.
 */
export function rentApiUrl(path: string): string {
  if (path.startsWith(LEGACY_FASTAPI_PREFIX)) {
    return FASTAPI_BASE + path.slice(LEGACY_FASTAPI_PREFIX.length);
  }
  return path;
}

/** Read the admin-view toggle from localStorage (client-side only). */
function shouldAppendAllOwners(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(ADMIN_VIEW_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * Append `?all=true` to GET URLs when the admin-view toggle is ON.
 * Only applied to collection-style endpoints — `/landlord` is the user's own
 * profile and never accepts `all=true`, so we skip it.
 *
 * Backend ignores the param for non-ADMIN users (verified in
 * `backend/api/app/routers/*.py::_admin_all`), so this is purely a UI convenience.
 */
function withAdminQuery(path: string): string {
  if (!shouldAppendAllOwners()) return path;
  // Skip endpoints that aren't owner-scoped collections
  if (path.includes("/landlord")) return path;
  // Avoid duplicating the param if it's already present
  if (/[?&]all=true(\b|$)/.test(path)) return path;
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}all=true`;
}

export class RentApiError extends Error {
  status: number;
  detail: unknown;
  constructor(status: number, detail: unknown, message: string) {
    super(message);
    this.status = status;
    this.detail = detail;
  }
}

async function parse(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const ct = res.headers.get("content-type") || "";
  if (ct.includes("application/json")) return res.json();
  return res.text();
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const isForm = body instanceof FormData;
  // Only inject `?all=true` on GET requests; mutations stay user-scoped.
  const finalPath = method === "GET" ? withAdminQuery(path) : path;
  const res = await apiFetch(rentApiUrl(finalPath), {
    method,
    headers: isForm
      ? undefined
      : body !== undefined
      ? { "Content-Type": "application/json" }
      : undefined,
    body: isForm ? (body as FormData) : body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const detail = await parse(res).catch(() => null);
    const body = typeof detail === "object" && detail ? (detail as Record<string, unknown>) : null;
    const msg =
      body && "detail" in body
        ? String(body.detail)
        : body && "error" in body
        ? String(body.error)
        : `Request failed (${res.status})`;
    throw new RentApiError(res.status, detail, msg);
  }
  return (await parse(res)) as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body?: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body?: unknown) => request<T>("PATCH", path, body),
  del: <T>(path: string) => request<T>("DELETE", path),
};

/**
 * Escape hatch for the handful of call sites that want the raw `Response`
 * rather than a parsed body — the landlord lookup (200 with a `null` body when
 * no profile exists), and the receipt lookups that treat 404 as "no receipt yet".
 *
 * Same URL mapping and same auth as `api.*`; it just does not throw.
 */
export function rentFetch(path: string, init?: RequestInit): Promise<Response> {
  return apiFetch(rentApiUrl(path), init);
}
